import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const ServicesPage = () => {
  const navigate = useNavigate();

  // ---------- HERO CHECK-LIST ----------
  const checkItems = [
    "Web Development",
    "Mobile App Development",
    "E-Commerce",
    "Salesforce",
    "Staff Augmentation",
  ];

  // ---------- DETAILED SERVICES ----------
  const services = [
    {
      id: "web-development",
      title: "Web Development",
      content:
        "Our web development services go beyond just creating websites — we craft digital experiences that align with your brand and business goals. We specialize in responsive and mobile-first designs, ensuring your site looks great on every device. Our team leverages modern frameworks such as React, Angular, and Vue.js to build dynamic and interactive interfaces. With a focus on clean coding standards, SEO optimization, and accessibility, we make sure your website reaches the right audience. We also integrate powerful backend solutions using Node.js, Express, and MongoDB for scalable and secure applications. From single-page applications (SPA) to full-stack enterprise solutions, we deliver high-performance websites that are future-ready.",
      image: "/web2.jpg",
      link: "/web",
    },
    {
      id: "mobile-app-development",
      title: "Mobile App Development",
      content:
        "We design and develop cutting-edge mobile applications tailored for both Android and iOS platforms. Our expertise spans native app development using Swift for iOS and Kotlin for Android, delivering high-performance, secure, and reliable apps. We also specialize in cross-platform solutions with Flutter and React Native, enabling faster development and cost-effective deployment without compromising quality.",
      image: "/mob1.jpg",
      link: "/mob",
    },
    {
      id: "ecommerce",
      title: "E-Commerce Solutions",
      content:
        "At Codex Future Technologies, we help businesses establish a strong online presence through customized E-Commerce solutions. From small startups to large enterprises, our team creates scalable, secure, and user-friendly e-commerce platforms tailored to your business needs. We focus on performance, seamless UX, and high conversion optimization.",
      image: "/E-commerce.jpg",
      link: "/ecommerce",
    },
    {
      id: "salesforce",
      title: "Salesforce Solutions",
      content:
        "We help businesses leverage the power of Salesforce to streamline their sales, marketing, and customer support processes. Our team specializes in customizing Salesforce to align perfectly with your business goals, ensuring maximum efficiency and ROI. From setup to automation, we help you maximize CRM potential.",
      image: "/sale.jpg",
      link: "/salesforce",
    },
    {
      id: "staff-augmentation",
      title: "Staff Augmentation",
      content:
        "Our Staff Augmentation services provide businesses with flexible access to skilled IT professionals, allowing you to scale your team quickly and efficiently. Whether you need short-term project support or long-term expertise, we connect you with the right talent to meet your goals.",
      image: "/staff.png",
      link: "/staff",
    },
  ];

  return (
    <div className="relative w-full min-h-screen bg-white overflow-hidden font-sans">
      {/* -------------------------------------------------
          HERO SECTION
      ------------------------------------------------- */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Blue background circle */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-70 -z-10 translate-x-32 -translate-y-20"></div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* LEFT – TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-sm text-blue-600 font-semibold uppercase tracking-widest">
              OUR SERVICES
            </p>
            <h1 className="mt-2 text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight">
              Professional Services Designed
              <br />
              To Support Your Every Need.
            </h1>

            <p className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed">
              Offers premium and elegant design with balanced structure and smooth flow.
              Built for efficiency and modern performance. Delivers flexible, responsive layouts with seamless interaction.
            </p>

            {/* DIGITAL EXCELLENCE CARD */}
            <div className="mt-8 p-6 bg-blue-600 rounded-2xl text-white max-w-md">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.75 17L9 20l-1.5-3-3-1.5 3-1.5L9 11l1.5 3 3 1.5-3 1.5zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold">Digital Excellence</h3>
              </div>
            </div>

            {/* CHECK-LIST */}
            <div className="mt-8 grid grid-cols-2 gap-3 text-gray-700">
              {checkItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-blue-600 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT – LAPTOP IMAGE */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <img
              src="/lp.png"
              alt="Laptop showing services dashboard"
              className="w-full max-w-lg mx-auto rounded-3xl shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* -------------------------------------------------
          DETAILED SERVICE CARDS
      ------------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-28">
        {services.map((service, idx) => {
          const isLeft = idx % 2 === 0;

          return (
            <div
              key={service.id}
              className={`flex flex-col ${
                isLeft ? "md:flex-row" : "md:flex-row-reverse"
              } items-center gap-12 lg:gap-20`}
            >
              {/* IMAGE */}
              <motion.div
                className="w-full md:w-1/2"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-3xl shadow-xl hover:scale-105 transition-transform duration-300"
                />
              </motion.div>

              {/* TEXT */}
              <motion.div
                className="w-full md:w-1/2 text-center md:text-left"
                initial={{ opacity: 0, x: isLeft ? -60 : 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-4">
                  {service.title}
                </h2>
                <p className="text-gray-600 leading-relaxed">{service.content}</p>

                {/* EXPLORE BUTTON */}
                <button
                  onClick={() => navigate(service.link)}
                  className="mt-6 inline-block bg-blue-600 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-blue-700 transition"
                >
                  Explore
                </button>
              </motion.div>
            </div>
          );
        })}
      </section>~
    </div>
  );
};

export default ServicesPage;
